import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  ChartLine, UsersThree, Calculator, Package, ShoppingBag,
  ShoppingCart, Bank, Buildings, ChatCircleText, PaperPlaneTilt,
  Gear, BookOpen, UsersFour, CreditCard, CheckCircle, Briefcase, Repeat,
  SignOut, House, Crown, List, X, CaretLeft, CaretRight, Receipt, HandCoins, ArrowsLeftRight,
  FileText, ClockCounterClockwise,
} from "@phosphor-icons/react";
import MobileBottomNav from "@/components/MobileBottomNav";
import FloatingAIChat from "@/components/FloatingAIChat";

const groups = [
  { label: "Ringkasan", items: [{ to: "/app/dashboard", label: "Dashboard", icon: ChartLine }] },
  { label: "Master Data", items: [
    { to: "/app/partners", label: "Pelanggan & Vendor", icon: UsersThree, feature: "partners" },
    { to: "/app/inventory", label: "Barang & Jasa", icon: Package, feature: "inventory" },
  ]},
  { label: "Transaksi", items: [
    { to: "/app/sales", label: "Penjualan", icon: ShoppingBag, feature: "sales" },
    { to: "/app/sales-recurring", label: "Faktur Berulang", icon: Repeat, feature: "sales" },
    { to: "/app/purchases", label: "Pembelian", icon: ShoppingCart, feature: "purchases" },
    { to: "/app/approvals", label: "Approval Center", icon: CheckCircle, feature: "approvals" },
    { to: "/app/cashbank", label: "Kas & Bank", icon: Bank, feature: "cash_bank" },
    { to: "/app/fund-requests", label: "Pengajuan Dana", icon: HandCoins, feature: "cash_bank" },
    { to: "/app/payroll", label: "Penggajian", icon: UsersFour, feature: "payroll" },
    { to: "/app/projects", label: "Proyek", icon: Briefcase, feature: "projects" },
  ]},
  { label: "AI & Integrasi", items: [
    { to: "/app/bank-reconciliation", label: "Rekonsiliasi Bank AI", icon: ArrowsLeftRight, feature: "ai_chat" },
    { to: "/app/telegram", label: "Telegram Bot", icon: PaperPlaneTilt, feature: "telegram" },
    { to: "/app/whatsapp", label: "WhatsApp Bot", icon: PaperPlaneTilt, feature: "whatsapp" },
  ]},
  { label: "Aset & Buku Besar", items: [
    { to: "/app/fixed-assets", label: "Aktiva Tetap", icon: Buildings, feature: "fixed_assets" },
    { to: "/app/accounting", label: "Akuntansi", icon: Calculator, feature: "accounting" },
    { to: "/app/tax-regulations", label: "Regulasi Pajak", icon: Receipt, feature: "accounting" },
  ]},
  { label: "Laporan", items: [
    { to: "/app/reports", label: "Laporan (Buku Besar)", icon: BookOpen, feature: "reports" },
    { to: "/app/custom-reports", label: "Laporan Custom", icon: BookOpen, feature: "reports" },
    { to: "/app/advanced-reports", label: "Laporan Lanjutan", icon: BookOpen, feature: "reports" },
    { to: "/app/annual-report", label: "Laporan Tahunan PT", icon: FileText, feature: "reports" },
    { to: "/app/ebupot", label: "e-Bupot PPh", icon: BookOpen, feature: "ebupot" },
    { to: "/app/audit-log", label: "Audit Log", icon: ClockCounterClockwise, adminOnly: true },
  ]},
  { label: "Admin", items: [
    { to: "/app/team", label: "Team", icon: UsersFour },
    { to: "/app/roles", label: "Role & Akses", icon: Gear },
    { to: "/app/settings", label: "Pengaturan", icon: Gear },
    { to: "/app/billing", label: "Billing", icon: CreditCard },
    { to: "/app/panduan", label: "Panduan", icon: BookOpen },
  ]},
];

export default function AppLayout() {
  const { user, workspace, logout } = useAuth();
  const [quota, setQuota] = useState(null);
  const [features, setFeatures] = useState(null);
  const [pendingAppr, setPendingAppr] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("nusa_sidebar_collapsed") === "1");
  const toggleCollapsed = () => setCollapsed((c) => { const n = !c; localStorage.setItem("nusa_sidebar_collapsed", n ? "1" : "0"); return n; });
  const location = useLocation();

  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    let alive = true;
    api.get("/auth/features").then((r) => { if (alive) setFeatures(r.data.features || {}); }).catch(() => {});
    return () => { alive = false; };
  }, []);
  useEffect(() => {
    if (!features?.ai_chat) return;
    let alive = true;
    const load = async () => { try { const r = await api.get("/ai/quota"); if (alive) setQuota(r.data); } catch {} };
    load();
    const t = setInterval(load, 30000);
    const onEvt = () => load();
    window.addEventListener("nusa:quota-updated", onEvt);
    return () => { alive = false; clearInterval(t); window.removeEventListener("nusa:quota-updated", onEvt); };
  }, [features]);

  useEffect(() => {
    if (!features?.approvals) return;
    let alive = true;
    const load = async () => { try { const r = await api.get("/approvals/pending-all"); if (alive) setPendingAppr(r.data.total || 0); } catch {} };
    load();
    const t = setInterval(load, 60000);
    return () => { alive = false; clearInterval(t); };
  }, [features, location.pathname]);

  return (
    <div className="flex min-h-screen bg-[#FDFDFC]" data-testid="app-shell">
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-slate-900/40 backdrop-blur-sm lg:hidden"
          onClick={() => setSidebarOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}
      <aside
        className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-[260px] ${collapsed ? "lg:w-[68px]" : "lg:w-[260px]"} shrink-0 border-r border-slate-200 bg-white flex flex-col transform transition-transform duration-200 ease-out lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}
        data-testid="app-sidebar"
      >
        <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between">
          <Link to="/" className="block" data-testid="brand-link">
            <div className={`font-serif-display text-2xl font-semibold text-slate-900 leading-none ${collapsed ? "lg:hidden" : ""}`}>AIAkuntan</div>
            <div className={`font-serif-display text-xl font-semibold text-amber-600 leading-none hidden ${collapsed ? "lg:block" : ""}`} title="AIAkuntan">AI</div>
            <div className={`text-[11px] uppercase tracking-widest text-slate-500 mt-1 ${collapsed ? "lg:hidden" : ""}`}>Akuntansi dengan Bantuan AI</div>
          </Link>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden text-slate-500 hover:text-slate-900 p-1" data-testid="sidebar-close-btn" aria-label="Tutup menu">
            <X size={18} />
          </button>
        </div>
        <nav className="flex-1 py-2 px-2 overflow-y-auto" data-testid="sidebar-nav">
          {user?.is_platform_admin && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-widest text-amber-600 px-3 py-1.5">Platform</div>
              <NavLink to="/app/superadmin" data-testid="nav-superadmin" title={collapsed ? "Super Admin" : undefined}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-1.5 text-sm rounded-md transition-colors ${collapsed ? "lg:justify-center lg:px-0" : ""} ${isActive ? "bg-amber-600 text-white" : "text-amber-700 hover:bg-amber-50"}`}>
                <Crown size={16} weight="fill" />
                <span className={collapsed ? "lg:hidden" : ""}>Super Admin</span>
              </NavLink>
            </div>
          )}
          {groups.map((g) => {
            const visibleItems = g.items.filter((it) => (!it.feature || features?.[it.feature]) && (!it.adminOnly || ["owner", "admin"].includes(user?.role)));
            if (!visibleItems.length) return null;
            return (
            <div key={g.label} className="mb-3">
              <div className={`text-[10px] uppercase tracking-widest text-slate-400 px-3 py-1.5 ${collapsed ? "lg:hidden" : ""}`}>{g.label}</div>
              {visibleItems.map(({ to, label, icon: Icon }) => (
                <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} title={collapsed ? label : undefined}
                  className={({ isActive }) => `relative flex items-center gap-3 px-3 py-1.5 text-sm rounded-md transition-colors ${collapsed ? "lg:justify-center lg:px-0" : ""} ${isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"}`}>
                  <Icon size={16} weight="regular" />
                  <span className={collapsed ? "lg:hidden" : ""}>{label}</span>
                  {label === "Approval Center" && pendingAppr > 0 && (
                    <span data-testid="nav-approvals-badge"
                      className={`bg-rose-600 text-white text-[10px] font-semibold rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center ${collapsed ? "lg:absolute lg:-top-1 lg:-right-1" : "ml-auto"}`}>
                      {pendingAppr > 99 ? "99+" : pendingAppr}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
            );
          })}
        </nav>
        <div className="border-t border-slate-200 p-3">
          <div className={`px-2 py-1 ${collapsed ? "lg:hidden" : ""}`}>
            <div className="text-[10px] text-slate-500 uppercase tracking-widest">Workspace</div>
            <div className="text-sm font-medium text-slate-900 truncate" data-testid="workspace-name">{workspace?.name || "—"}</div>
            <div className="text-xs text-amber-600 font-medium mt-0.5" data-testid="workspace-plan">Plan: {workspace?.plan || "free"}</div>
          </div>
          <button onClick={logout} className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md transition-colors mt-2 ${collapsed ? "lg:justify-center lg:px-0" : ""}`} data-testid="logout-btn" title="Keluar">
            <SignOut size={16} /><span className={collapsed ? "lg:hidden" : ""}>Keluar</span>
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-14 border-b border-slate-200 bg-white/80 backdrop-blur-md flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20" data-testid="app-header">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-slate-700 hover:text-slate-900 p-1 -ml-1"
              data-testid="sidebar-open-btn"
              aria-label="Buka menu"
            >
              <List size={22} />
            </button>
            <button
              onClick={toggleCollapsed}
              className="hidden lg:inline-flex text-slate-700 hover:text-slate-900 p-1"
              data-testid="sidebar-collapse-btn"
              aria-label={collapsed ? "Perbesar menu" : "Minimize menu"}
              title={collapsed ? "Perbesar menu" : "Minimize menu"}
            >
              {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 min-w-0">
              <House size={14} className="hidden sm:block shrink-0" />
              <span className="hidden sm:block text-slate-400">/</span>
              <span className="text-slate-900 font-medium truncate">{workspace?.name}</span>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {quota && <div className="text-right leading-tight hidden md:block" data-testid="ai-quota"><div className="text-[10px] uppercase tracking-widest text-slate-500">AI Quota</div><div className="text-xs font-medium text-slate-900 tabular-nums">{quota.unlimited ? "Unlimited" : `${quota.used} / ${quota.limit}`}</div></div>}
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-sm font-medium text-slate-900 truncate max-w-[160px]" data-testid="user-fullname">{user?.full_name}</div>
              <div className="text-xs text-slate-500 capitalize truncate max-w-[160px]">{user?.role} · {user?.email}</div>
            </div>
            <div className="w-8 h-8 rounded-full bg-slate-900 text-white flex items-center justify-center text-xs font-semibold shrink-0">{user?.full_name?.[0]?.toUpperCase() || "U"}</div>
          </div>
        </header>
        <div className="flex-1 min-h-0 overflow-auto pb-16 lg:pb-0"><Outlet /></div>
      </main>
      <MobileBottomNav features={features} onOpenMenu={() => setSidebarOpen(true)} />
      {features?.ai_chat && <FloatingAIChat />}
    </div>
  );
}
